import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  FormControl,
  FormGroup,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as virActions from "src/actions/virActions";
import * as appActions from "src/actions/appActions";
import getCountryFromURL from "src/utils/getCountryFromUrl";
import { useIsMount } from "src/hooks/useIsMount";
import useStyles from "src/layouts/DigitalVIRLayout/useStyles";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import { Typography, RoundedButton, TextField } from "@packages/ui";
import getBrandFromUrl from "src/utils/getBrandFromUrl";
import VirPhoneInput from "src/components/VirPhoneInput/VirPhoneInput";
import brandColors from "@packages/ui/theme/colors";

const DigitalVIRLogin = () => {
  const classes = useStyles();
  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      licence: "",
      registration: "",
    },
  });
  const dispatch = useDispatch();

  const athenaVirAuth = useSelector((state) => state.vir.athenaVirAuth);
  const athenaVirUser = useSelector((state) => state.vir.athenaVirUser);
  const athenaSource = useSelector((state) => state.vir.athenaSource.source);
  const [phoneNumber, setPhoneNumber] = useState("");
  const isMount = useIsMount();
  const brand = getBrandFromUrl().toLowerCase();
  const actualBrand = useSelector((state) => state.vir.athenaVirAuth.data?.brand) || brand;

  const [email, setEmail] = useState("");

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [showUserDetail, setShowUserDetail] = useState(false);
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  useEffect(() => {
    if (searchParams.get("source") === "app") {
      // from app must be staff
      const rego = searchParams.get("rego");
      const raNumber = searchParams.get("raNumber");
      const nonrevNumber = searchParams.get("nonrevNumber");
      let params = {};
      if (raNumber !== null || nonrevNumber !== null) {
        params =
          raNumber !== null
            ? {
                Registration: rego,
                Brand: brand,
                Country: getCountryFromURL(),
                Type: "staff",
                RaNumber: raNumber,
              }
            : {
                Registration: rego,
                Brand: brand,
                Country: getCountryFromURL(),
                Type: "staff",
                NonRevNumber: nonrevNumber,
              };
      } else {
        params = {
          Registration: rego,
          Brand: brand,
          Country: getCountryFromURL(),
          Type: "staff",
        };
        dispatch(
          virActions.virReportType({
            reportType: "unReported",
          })
        );
      }
      const virPayloadBE = { Registration: rego };
      dispatch(virActions.virAuth(params));
      dispatch(virActions.payloadBE(virPayloadBE));
    } else if (searchParams.get("source") === "sms") {
      // from sms must be customer
      const rego = searchParams.get("rego");
      const raNumber = searchParams.get("raNumber");
      const nonrevNumber = searchParams.get("nonrevNumber");
      const params =
        raNumber !== "null"
          ? {
              Registration: rego,
              Brand: brand,
              Country: getCountryFromURL(),
              Type: "customer",
              RaNumber: raNumber,
            }
          : {
              Registration: rego,
              Brand: brand,
              Country: getCountryFromURL(),
              Type: "customer",
              NonRevNumber: nonrevNumber,
            };
      const virPayloadBE = { Registration: rego };
      dispatch(virActions.virAuth(params));
      dispatch(virActions.payloadBE(virPayloadBE));
    }
  }, []);

  useEffect(() => {
    if (!isMount) {
      if (!athenaVirAuth.loading) {
        if (athenaVirAuth.data) {
          setShowUserDetail(true);
        } else {
          if (searchParams.get("source") !== "app" && searchParams.get("source") !== "sms") {
            dispatch(
              appActions.createToast({
                type: "failError",
                message: "Sorry, we're unable to locate your rental with the details provided. Please call us so we can help you with next steps.",
              })
            );
          }
        }

        if (
          athenaSource === "web" &&
          athenaVirAuth?.data &&
          athenaVirAuth?.data?.Status !== "Open"
        ) {
          navigate(`/vehicleincidents/rentalexpired?brand=${actualBrand}`, { replace: true });
        } else {
          if (athenaSource === "app") {
            if (searchParams.get("vir")) {
              navigate(
                `/vehicleincidents/incidentselection?source=app&vir=${searchParams.get(
                  "vir"
                )}&virType=${searchParams.get("virType")}&brand=${actualBrand}`,
                { replace: true }
              );
            } else {
              dispatch(
                virActions.virAppLoading({
                  loading: false,
                })
              );
              navigate(`/vehicleincidents/incidentselection?brand=${actualBrand}`, {
                replace: true,
              });
            }
          } else if (athenaSource === "sms") {
            navigate(
              `/vehicleincidents/incidentselection?source=sms&vir=${searchParams.get(
                "vir"
              )}&virType=${searchParams.get("virType")}&brand=${actualBrand}`,
              { replace: true }
            );
          }
        }
      }
    }
  }, [athenaVirAuth.loading]);

  useEffect(() => {
    if (!isMount) {
      if (!athenaVirUser.loading) {
        if (athenaVirUser?.data === "success") {
          if (athenaVirAuth?.data && searchParams.get("source") === "app") {
            navigate(`/vehicleincidents/incidentselection?brand=${actualBrand}`);
          } else if (athenaVirAuth?.data && athenaVirAuth?.data?.Status === "Open") {
            navigate(`/vehicleincidents/incidentselection?brand=${actualBrand}`);
          }
        } else if (athenaVirUser?.error) {
          dispatch(appActions.createToast({ type: "error", message: "Failed to update user" }));
        }
      }
    }
  }, [athenaVirUser.loading]);

  useEffect(() => {
    if (athenaVirAuth.data?.phoneNumber) {
      setPhoneNumber(athenaVirAuth.data.phoneNumber);
    }
    if (athenaVirAuth.data?.email) {
      setEmail(athenaVirAuth.data.email);
    }
  }, [athenaVirAuth.data?.phoneNumber, athenaVirAuth.data?.email]);
  // for adding hypen
  const formatRegistration = (registration) => {
    if (!registration) {
      return "";
    }
    // Remove existing hyphens and convert to uppercase
    const cleanLicense = registration.replace(/-/g, "").toUpperCase();

    // Determine the format based on the length of the license
    let formattedLicense;
    if (cleanLicense.length === 5) {
      formattedLicense = cleanLicense.slice(0, 2) + "-" + cleanLicense.slice(2);
    } else if (cleanLicense.length === 6) {
      formattedLicense = cleanLicense.slice(0, 3) + "-" + cleanLicense.slice(3);
    } else if (cleanLicense.length === 7) {
      formattedLicense = cleanLicense.slice(0, 4) + "-" + cleanLicense.slice(4);
    } else {
      throw new Error("Invalid license length");
    }
    return formattedLicense;
  };

  const handleLogin = () => {
    const data = getValues();
    const formattedRegistration = formatRegistration(data.registration);
    const params = {
      Registration: formattedRegistration,
      Licence: data.licence,
      Brand: brand,
      Country: getCountryFromURL(),
      Type: "customer",
    };
    const virPayloadBE = { Registration: formattedRegistration, licence: data.licence };

    dispatch(virActions.virAuth(params));
    dispatch(virActions.payloadBE(virPayloadBE));
  };

  const handleUserDetail = () => {
    if (!email || !isValidEmail(email)) {
      dispatch(
        appActions.createToast({
          type: "error",
          message: "Please enter a valid email address before continuing.",
        })
      );
      return;
    }

    const userPayload = {
      email: email,
      phoneNumber: phoneNumber,
      serviceTrackingId: athenaVirAuth.data.serviceTrackingId,
    };
    dispatch(virActions.userVir(userPayload));
  };

  return (
    <>
      <h1 className={classes.heading}>
        Please provide your details so we can find your rental information.
      </h1>
      <hr
                className={classes.breakLine}
                style={{ borderColor: brandColors[brand] }}
              />
      <Box justifyContent="center" alignItems="center" display="flex" width="100%">
        <Box justifyContent="center" alignItems="center" className={classes.textFieldsBox}>
          <FormGroup>
            <FormControl margin="normal">
              <Controller
                name="licence"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Drivers Licence Number"
                    variant="outlined"
                    fullWidth
                    // size={isMobile ? "small" : "medium"}
                    error={!!errors.licence}
                    helperText={errors.licence?.message}
                    InputLabelProps={{
                      classes: { root: classes.noWrapLabel },
                      style: { fontSize: "1rem" }, // smaller font for label
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl margin="normal">
              <Controller
                name="registration"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Vehicle Registration"
                    variant="outlined"
                    fullWidth
                    helperText={errors.registration?.message}
                    InputLabelProps={{
                      classes: { root: classes.noWrapLabel },
                      style: { fontSize: "1rem" }, // smaller font for label
                    }}
                  />
                )}
              />
            </FormControl>

            {showUserDetail ? (
              <>
                <Typography className={classes.heading}>
                  Ensure your contact details are current.
                </Typography>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <VirPhoneInput
                          phoneNumber={phoneNumber}
                          onChange={(value) => {
                            setPhoneNumber(value);
                          }}
                        />
                      </Box>
                    </Grid>
                  </Box>
                </Grid>

                <FormControl margin="normal">
                  <TextField
                    variant="outlined"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  ></TextField>
                </FormControl>
                <div>
                  <RoundedButton
                    onClick={handleUserDetail}
                    variant="contained"
                    type="button"
                    fullWidth
                    disabled={athenaVirUser.loading}
                  >
                    {athenaVirUser.loading ? <CircularProgress /> : "Continue"}
                  </RoundedButton>
                </div>
              </>
            ) : (
              <div>
                <RoundedButton
                  onClick={handleLogin}
                  variant="contained"
                  fullWidth
                  disabled={athenaVirAuth.loading}
                >
                  {athenaVirAuth.loading ? <CircularProgress /> : "Continue"}
                </RoundedButton>
              </div>
            )}
          </FormGroup>
        </Box>
      </Box>
    </>
  );
};

export default DigitalVIRLogin;
